import React from 'react';
import { Card, Table, Tag, Tooltip, Statistic, Row, Col, Empty } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { ObserveAuditItem, ObserveOp } from '../../../../services/devApi';

const OP_CONFIG: Record<ObserveOp, { color: string; label: string }> = {
  I: { color: 'green', label: '新增' },
  U: { color: 'orange', label: '更新' },
  D: { color: 'red', label: '刪除' },
};

export function OpTag({ op }: { op: ObserveOp }): JSX.Element {
  const cfg = OP_CONFIG[op] || { color: 'default', label: op };
  return <Tag color={cfg.color}>{cfg.label}</Tag>;
}

function formatTime(val: string): string {
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return val;
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

function fmtCell(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

interface KvRow {
  column: string;
  value: string;
}

interface DiffRow {
  column: string;
  oldVal: string;
  newVal: string;
}

function renderExpanded(rec: ObserveAuditItem): React.ReactNode {
  if (rec.op === 'U') {
    const rows: DiffRow[] = (rec.changedColumns || []).map((c) => ({
      column: c,
      oldVal: fmtCell(rec.oldRow?.[c]),
      newVal: fmtCell(rec.newRow?.[c]),
    }));
    if (rows.length === 0) return <Empty description="無變更欄位明細" />;
    const cols: ColumnsType<DiffRow> = [
      { title: '欄位', dataIndex: 'column', key: 'column', width: 200, render: (v: string) => <span className="font-mono">{v}</span> },
      { title: '舊值', dataIndex: 'oldVal', key: 'oldVal', render: (v: string) => <span className="font-mono text-red-600">{v || '(空)'}</span> },
      { title: '新值', dataIndex: 'newVal', key: 'newVal', render: (v: string) => <span className="font-mono text-green-600">{v || '(空)'}</span> },
    ];
    return <Table<DiffRow> dataSource={rows} columns={cols} size="small" pagination={false} rowKey="column" />;
  }

  const source = rec.op === 'I' ? rec.newRow : rec.oldRow;
  const rows: KvRow[] = Object.entries(source || {})
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => ({ column: k, value: fmtCell(v) }));
  if (rows.length === 0) return <Empty description="無欄位資料" />;
  const cols: ColumnsType<KvRow> = [
    { title: '欄位', dataIndex: 'column', key: 'column', width: 200, render: (v: string) => <span className="font-mono">{v}</span> },
    { title: '值', dataIndex: 'value', key: 'value', render: (v: string) => <span className="font-mono">{v}</span> },
  ];
  return <Table<KvRow> dataSource={rows} columns={cols} size="small" pagination={false} rowKey="column" />;
}

interface Props {
  audits: ObserveAuditItem[];
  auditCount: number;
  whitelistTables: string[];
}

const LiveAuditPanel: React.FC<Props> = ({ audits, auditCount, whitelistTables }) => {
  const whitelistSet = new Set(whitelistTables);
  const tableCount = new Set(audits.map((a) => a.tableName)).size;

  const columns: ColumnsType<ObserveAuditItem> = [
    {
      title: '時間',
      dataIndex: 'at',
      key: 'at',
      width: 100,
      render: (val: string) => <span className="font-mono">{formatTime(val)}</span>,
    },
    {
      title: '表名',
      dataIndex: 'tableName',
      key: 'tableName',
      width: 220,
      render: (name: string) => (
        <Tag color={whitelistSet.has(name) ? 'blue' : 'default'} className="font-mono">
          {name}
        </Tag>
      ),
    },
    {
      title: '操作',
      dataIndex: 'op',
      key: 'op',
      width: 80,
      render: (op: ObserveOp) => <OpTag op={op} />,
    },
    {
      title: '變更欄位',
      dataIndex: 'changedColumns',
      key: 'changedColumns',
      ellipsis: { showTitle: false },
      render: (cols: string[]) => {
        const txt = (cols || []).join(', ');
        if (!txt) return <span className="text-gray-400">--</span>;
        return (
          <Tooltip title={txt} placement="topLeft">
            <span className="font-mono">{txt}</span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Card title="即時寫入回執">
      <Row gutter={16} className="mb-4">
        <Col>
          <Statistic title="總寫入筆數" value={auditCount} />
        </Col>
        <Col>
          <Statistic title="涉及表數" value={tableCount} />
        </Col>
      </Row>
      {audits.length > 0 ? (
        <Table<ObserveAuditItem>
          dataSource={audits}
          columns={columns}
          rowKey="id"
          size="small"
          scroll={{ x: 700 }}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          expandable={{ expandedRowRender: renderExpanded }}
        />
      ) : (
        <Empty description="尚無寫入紀錄，請開始操作 ERP" />
      )}
    </Card>
  );
};

export default LiveAuditPanel;
